<template>
  <PrimevueSidebar
    :visible="visible"
    position="right"
    modal
    dismissable
    :block-scroll="true"
    :show-close-icon="false"
    class="support-ticket-rightbar"
    :pt="sidebarPassThrough"
    @show="handleShow"
    @update:visible="handleVisibilityUpdate"
    @after-hide="handleAfterHide"
  >
    <template #header>
      <div class="support-ticket-rightbar-header">
        <div>
          <h2 :id="titleId" class="support-ticket-rightbar-title">Contacter le support</h2>
          <small>Assistance Suite241</small>
        </div>
        <button
          type="button"
          class="support-ticket-collapse-button"
          :disabled="isSubmitting"
          title="Réduire"
          aria-label="Réduire le formulaire de support"
          @click="collapseRightbar"
        >
          <i class="fa fa-chevron-right" aria-hidden="true"></i>
        </button>
      </div>
    </template>

    <form ref="supportForm" class="support-ticket-form" @submit.prevent="submitTicket">
      <p class="text-muted small mb-3">Décrivez le problème rencontré. Les informations de votre compte et du site courant seront jointes automatiquement.</p>
      <div v-if="errorMessage" class="alert alert-danger py-2">{{ errorMessage }}</div>

      <div class="mb-3">
        <label class="form-label fw-semibold">Module concerné *</label>
        <PrimevueDropdown
          ref="moduleDropdown"
          v-model="form.module"
          :options="moduleOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Sélectionner un module"
          appendTo="self"
          class="w-100"
          :disabled="isSubmitting"
        />
      </div>

      <div class="mb-3">
        <label class="form-label fw-semibold">Description du problème *</label>
        <PrimevueTextarea v-model="form.description" rows="6" maxlength="5000" autoResize class="w-100" placeholder="Expliquez ce que vous faisiez, ce qui s’est produit et le résultat attendu. Vous pouvez utiliser des retours à la ligne et des listes à tirets." :disabled="isSubmitting" />
        <div class="d-flex justify-content-between gap-3 mt-1">
          <small class="text-muted">20 caractères minimum.</small>
          <small class="text-muted">{{ form.description.length }} / 5 000</small>
        </div>
      </div>

      <div v-if="showContactFields" class="row g-3 mb-3">
        <div class="col-sm-6">
          <label class="form-label fw-semibold" for="supportContactPhone">Téléphone pour appel</label>
          <PrimevueInputText
            id="supportContactPhone"
            v-model="form.contact_phone"
            type="tel"
            inputmode="tel"
            class="w-100"
            placeholder="+241..."
            :disabled="isSubmitting"
            @input="sanitizeContactPhone"
          />
        </div>
        <div class="col-sm-6">
          <label class="form-label fw-semibold" for="supportContactWhatsApp">Numéro WhatsApp</label>
          <PrimevueInputText
            id="supportContactWhatsApp"
            v-model="form.contact_whatsapp_phone"
            type="tel"
            inputmode="tel"
            class="w-100"
            placeholder="+241..."
            :disabled="isSubmitting"
            @input="sanitizeContactWhatsApp"
          />
        </div>
        <small class="text-muted">Facultatif — ces numéros sont joints uniquement à cette demande.</small>
      </div>

      <div class="mb-2">
        <label class="form-label fw-semibold">Captures d’écran</label>
        <input ref="screenshotsInput" class="visually-hidden" type="file" accept="image/jpeg,image/png,image/webp" :disabled="isSubmitting || screenshots.length >= 5" @change="onScreenshotSelected" />
        <button type="button" class="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2" :disabled="isSubmitting || screenshots.length >= 5" @click="openScreenshotPicker">
          <i class="fa fa-image" aria-hidden="true"></i>
          Ajouter une capture
        </button>
        <small class="text-muted d-block mt-1">Jusqu’à 5 images JPG, PNG ou WebP, 5 Mo maximum par fichier.</small>
      </div>

      <div v-if="screenshots.length" class="support-ticket-previews">
        <article v-for="(screenshot, index) in screenshots" :key="screenshot.previewUrl" class="support-ticket-preview">
          <img :src="screenshot.previewUrl" :alt="`Capture ${index + 1}`" />
          <span :title="screenshot.file.name">{{ screenshot.file.name }}</span>
          <button type="button" class="btn btn-outline-danger btn-sm" :disabled="isSubmitting" :aria-label="`Retirer ${screenshot.file.name}`" @click="removeScreenshot(index)">
            <i class="fa fa-xmark" aria-hidden="true"></i>
          </button>
        </article>
      </div>

      <div class="support-ticket-actions">
        <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="isSubmitting" @click="cancelTicket">
          <i class="fa fa-xmark me-1" aria-hidden="true"></i>Annuler
        </button>
        <AppLoadingButton type="submit" class="btn btn-primary btn-sm" :loading="isSubmitting" :disabled="!canSubmit" loading-label="Envoi...">
          <i class="fa fa-paper-plane me-1" aria-hidden="true"></i> Envoyer au support
        </AppLoadingButton>
      </div>
    </form>
  </PrimevueSidebar>
</template>

<script>
import { defineComponent } from 'vue';
import PrimevueSidebar from 'primevue/sidebar';
import pkg from '../../package.json';
import AppLoadingButton from '@/components/AppLoadingButton.vue';
import { CASHIER_SUPPORT_TICKET_MODULES } from '@/support/supportTicketModules';
import { sanitizePhoneInput } from '@/utils/phone';

const MAX_SCREENSHOTS = 5;
const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024;
const ACCEPTED_SCREENSHOT_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const RIGHTBAR_ID = 'support-ticket-rightbar';
const RIGHTBAR_TITLE_ID = 'support-ticket-rightbar-title';

export default defineComponent({
  name: 'SupportTicketRightbar',
  components: { AppLoadingButton, PrimevueSidebar },
  props: {
    visible: { type: Boolean, default: false },
    moduleOptions: { type: Array, default: () => CASHIER_SUPPORT_TICKET_MODULES },
    variant: { type: String, default: 'admin' },
  },
  emits: ['update:visible', 'after-hide'],
  data() {
    return {
      form: { module: '', description: '', contact_phone: '', contact_whatsapp_phone: '' },
      screenshots: [],
      errorMessage: '',
      isSubmitting: false,
      panelTop: 0,
      panelBottom: 0,
      boundaryResizeObserver: null,
      boundaryFrame: null,
      boundaryListenersActive: false,
    };
  },
  computed: {
    currentUser() {
      return this.$store?.getters?.['auth/currentUser'] || null;
    },
    showContactFields() {
      // Temporary contact inputs are useful only when neither profile phone can be trusted by the API.
      return !String(this.currentUser?.telephone1 || '').trim()
        && !String(this.currentUser?.telephone2 || '').trim();
    },
    canSubmit() {
      const length = this.form.description.trim().length;
      return !this.isSubmitting && Boolean(this.form.module) && length >= 20 && length <= 5000;
    },
    titleId() {
      return RIGHTBAR_TITLE_ID;
    },
    sidebarPassThrough() {
      return {
        mask: {
          class: 'support-ticket-rightbar-mask',
          style: {
            top: `${this.panelTop}px`,
            bottom: `${this.panelBottom}px`,
            height: 'auto',
          },
        },
        root: {
          id: RIGHTBAR_ID,
          role: 'dialog',
          'aria-modal': 'true',
          'aria-labelledby': RIGHTBAR_TITLE_ID,
        },
      };
    },
  },
  watch: {
    visible(isVisible) {
      if (isVisible) {
        this.updatePanelBounds();
        this.$nextTick(this.startBoundaryTracking);
      } else {
        this.stopBoundaryTracking();
      }
    },
  },
  mounted() {
    if (this.visible) {
      this.updatePanelBounds();
      this.startBoundaryTracking();
    }
  },
  beforeUnmount() {
    this.stopBoundaryTracking();
    this.revokePreviews();
  },
  methods: {
    layoutElements() {
      const isCashier = this.variant === 'cashier';
      const layout = document.querySelector(isCashier ? '.cashier-shell' : '.admin-layout');

      return {
        navbar: layout?.querySelector(isCashier ? '[data-tour="cashier-navbar"]' : '[data-tour="admin-navbar"]') || null,
        footer: layout?.querySelector(isCashier ? '.cashier-footer' : '.app-footer') || null,
      };
    },
    updatePanelBounds() {
      if (typeof window === 'undefined') return;

      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const { navbar, footer } = this.layoutElements();
      const navbarRect = navbar?.getBoundingClientRect();
      const footerRect = footer?.getBoundingClientRect();
      const top = navbarRect ? Math.max(0, Math.min(viewportHeight, navbarRect.bottom)) : 0;
      const footerIsVisible = footerRect && footerRect.top < viewportHeight && footerRect.bottom > 0;
      const bottom = footerIsVisible
        ? Math.max(0, viewportHeight - Math.max(0, footerRect.top))
        : 0;

      this.panelTop = Math.round(top);
      this.panelBottom = Math.round(Math.min(bottom, Math.max(0, viewportHeight - top)));
    },
    schedulePanelBoundsUpdate() {
      if (this.boundaryFrame !== null) return;

      const update = () => {
        this.boundaryFrame = null;
        this.updatePanelBounds();
      };
      this.boundaryFrame = typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame(update)
        : window.setTimeout(update, 16);
    },
    startBoundaryTracking() {
      if (this.boundaryListenersActive || typeof window === 'undefined') return;

      this.boundaryListenersActive = true;
      this.updatePanelBounds();
      window.addEventListener('resize', this.schedulePanelBoundsUpdate);
      window.addEventListener('scroll', this.schedulePanelBoundsUpdate, true);

      if (typeof window.ResizeObserver === 'function') {
        const { navbar, footer } = this.layoutElements();
        this.boundaryResizeObserver = new window.ResizeObserver(this.schedulePanelBoundsUpdate);
        if (navbar) this.boundaryResizeObserver.observe(navbar);
        if (footer) this.boundaryResizeObserver.observe(footer);
      }
    },
    stopBoundaryTracking() {
      if (typeof window !== 'undefined' && this.boundaryListenersActive) {
        window.removeEventListener('resize', this.schedulePanelBoundsUpdate);
        window.removeEventListener('scroll', this.schedulePanelBoundsUpdate, true);
      }
      this.boundaryListenersActive = false;
      this.boundaryResizeObserver?.disconnect();
      this.boundaryResizeObserver = null;

      if (this.boundaryFrame !== null && typeof window !== 'undefined') {
        if (typeof window.cancelAnimationFrame === 'function') {
          window.cancelAnimationFrame(this.boundaryFrame);
        } else {
          window.clearTimeout(this.boundaryFrame);
        }
        this.boundaryFrame = null;
      }
    },
    handleShow() {
      this.startBoundaryTracking();
      this.$nextTick(() => {
        const dropdown = this.$refs.moduleDropdown?.$el || this.$refs.moduleDropdown;
        dropdown?.focus?.();
      });
    },
    handleVisibilityUpdate(nextVisible) {
      if (!nextVisible && this.isSubmitting) return;
      this.$emit('update:visible', nextVisible);
    },
    handleAfterHide() {
      this.stopBoundaryTracking();
      this.$emit('after-hide');
    },
    collapseRightbar() {
      if (!this.isSubmitting) this.$emit('update:visible', false);
    },
    openScreenshotPicker() {
      this.$refs.screenshotsInput?.click();
    },
    onScreenshotSelected(event) {
      this.errorMessage = '';
      const selectedFiles = Array.from(event.target?.files || []);
      const remainingSlots = MAX_SCREENSHOTS - this.screenshots.length;

      selectedFiles.slice(0, remainingSlots).forEach((file) => {
        if (!ACCEPTED_SCREENSHOT_TYPES.includes(file.type)) {
          this.errorMessage = 'Seules les images JPG, PNG et WebP sont acceptées.';
        } else if (file.size > MAX_SCREENSHOT_BYTES) {
          this.errorMessage = `La capture « ${file.name} » dépasse 5 Mo.`;
        } else {
          this.screenshots.push({ file, previewUrl: URL.createObjectURL(file) });
        }
      });
      if (this.$refs.screenshotsInput) this.$refs.screenshotsInput.value = '';
    },
    removeScreenshot(index) {
      const [removed] = this.screenshots.splice(index, 1);
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
    },
    revokePreviews() {
      this.screenshots.forEach(({ previewUrl }) => URL.revokeObjectURL(previewUrl));
    },
    resetForm() {
      this.revokePreviews();
      this.form = { module: '', description: '', contact_phone: '', contact_whatsapp_phone: '' };
      this.screenshots = [];
      this.errorMessage = '';
      if (this.$refs.screenshotsInput) this.$refs.screenshotsInput.value = '';
    },
    cancelTicket() {
      if (this.isSubmitting) return;
      this.resetForm();
      this.$emit('update:visible', false);
    },
    apiErrorMessage(error) {
      const errors = error?.response?.data?.errors;
      return errors ? Object.values(errors).flat().join(' ') : 'Impossible d’envoyer votre demande pour le moment.';
    },
    sanitizeContactPhone(event) {
      this.form.contact_phone = sanitizePhoneInput(event?.target?.value ?? this.form.contact_phone);
    },
    sanitizeContactWhatsApp(event) {
      this.form.contact_whatsapp_phone = sanitizePhoneInput(event?.target?.value ?? this.form.contact_whatsapp_phone);
    },
    async submitTicket() {
      if (!this.canSubmit) return;
      this.isSubmitting = true;
      this.errorMessage = '';
      try {
        const payload = new FormData();
        payload.append('module', this.form.module);
        payload.append('description', this.form.description);
        payload.append('page_url', this.$route?.fullPath || window.location.pathname);
        payload.append('app_version', pkg.version || '');
        if (this.showContactFields && this.form.contact_phone) {
          payload.append('contact_phone', sanitizePhoneInput(this.form.contact_phone));
        }
        if (this.showContactFields && this.form.contact_whatsapp_phone) {
          payload.append('contact_whatsapp_phone', sanitizePhoneInput(this.form.contact_whatsapp_phone));
        }
        this.screenshots.forEach(({ file }) => payload.append('screenshots[]', file));
        await this.$api.post('/support-tickets', payload, { headers: { 'Content-Type': 'multipart/form-data' }, skipErrorToast: true });
        this.resetForm();
        this.$emit('update:visible', false);
      } catch (error) {
        this.errorMessage = this.apiErrorMessage(error);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
});
</script>

<style>
.support-ticket-rightbar-mask {
  align-items: stretch !important;
  z-index: 1025 !important;
}

.support-ticket-rightbar.p-sidebar {
  border-left: 1px solid rgba(15, 23, 42, 0.12);
  box-shadow: -18px 0 42px rgba(15, 23, 42, 0.2);
  height: 100% !important;
  max-width: 500px;
  width: 100vw !important;
}

.support-ticket-rightbar .p-sidebar-header {
  border-bottom: 1px solid #e2e8f0;
  padding: 1rem 1.15rem;
}

.support-ticket-rightbar .p-sidebar-header-content {
  width: 100%;
}

.support-ticket-rightbar .p-sidebar-content {
  display: flex;
  padding: 0;
}

.support-ticket-rightbar-header {
  align-items: center;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  width: 100%;
}

.support-ticket-rightbar-title {
  color: #0f172a;
  font-size: 1.05rem;
  font-weight: 800;
  margin: 0;
}

.support-ticket-rightbar-header small {
  color: #64748b;
}

.support-ticket-collapse-button {
  align-items: center;
  background: #f1f5f9;
  border: 0;
  border-radius: 999px;
  color: #334155;
  display: inline-flex;
  flex: 0 0 34px;
  height: 34px;
  justify-content: center;
  transition: background-color 0.18s ease, color 0.18s ease, transform 0.18s ease;
  width: 34px;
}

.support-ticket-collapse-button:hover,
.support-ticket-collapse-button:focus-visible {
  background: #e2e8f0;
  color: #0d6efd;
  transform: translateX(2px);
}

.support-ticket-collapse-button:disabled {
  cursor: wait;
  opacity: 0.6;
}

.support-ticket-form {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 100%;
  padding: 1.15rem;
  width: 100%;
}

.support-ticket-previews {
  display: grid;
  gap: 0.65rem;
  margin-top: 0.85rem;
}

.support-ticket-preview {
  align-items: center;
  border: 1px solid #e2e8f0;
  border-radius: 0.65rem;
  display: grid;
  gap: 0.75rem;
  grid-template-columns: 64px minmax(0, 1fr) auto;
  padding: 0.5rem;
}

.support-ticket-preview img {
  border-radius: 0.4rem;
  height: 48px;
  object-fit: cover;
  width: 64px;
}

.support-ticket-preview span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.support-ticket-actions {
  align-items: center;
  background: #ffffff;
  border-top: 1px solid #e2e8f0;
  bottom: 0;
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin: auto -1.15rem -1.15rem;
  padding: 1rem 1.15rem;
  position: sticky;
}

@media (prefers-reduced-motion: reduce) {
  .support-ticket-rightbar.p-sidebar,
  .support-ticket-rightbar.p-sidebar-enter-active,
  .support-ticket-rightbar.p-sidebar-leave-active,
  .support-ticket-collapse-button {
    transition: none !important;
  }
}
</style>
